import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Grid, Chip, TextField, InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, CircularProgress, Alert, Select, MenuItem, FormControl,
  InputLabel, IconButton, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogActions, Tabs, Tab, Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  SwapHoriz as ImpersonateIcon,
  Block as SuspendIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, AreaChart, Area,
} from 'recharts';
import api from '../api';

const StatCard = ({ title, value, icon, color }) => {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        p: 3, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2,
        border: `1px solid ${theme.palette.divider}`,
        background: theme.palette.background.paper,
      }}
    >
      <Box sx={{
        width: 48, height: 48, borderRadius: 2, display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        bgcolor: `${color}15`, color: color,
      }}>
        {icon}
      </Box>
      <Box>
        <Typography variant="body2" color="text.secondary">{title}</Typography>
        <Typography variant="h5" fontWeight={700}>{value}</Typography>
      </Box>
    </Paper>
  );
};

const formatCurrency = (val) => `$${Number(val || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const formatDate = (iso) => iso ? new Date(iso).toLocaleDateString() : '—';
const timeAgo = (iso) => {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

export default function PlatformAdmin() {
  const theme = useTheme();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [revenue, setRevenue] = useState({});
  const [activity, setActivity] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [error, setError] = useState('');
  const [detailDialog, setDetailDialog] = useState(null);
  const [impersonateDialog, setImpersonateDialog] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [statsRes, companiesRes, revenueRes, activityRes] = await Promise.all([
        api.get('/api/platform/stats/'),
        api.get('/api/platform/companies/', { params: { search, status: statusFilter !== 'all' ? statusFilter : undefined } }),
        api.get('/api/platform/revenue/'),
        api.get('/api/platform/activity/', { params: { limit: 20 } }),
      ]);
      setStats(statsRes.data);
      setCompanies(companiesRes.data.companies || []);
      setRevenue(revenueRes.data);
      setActivity(activityRes.data.activities || []);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('Access denied. Platform admin privileges required.');
      } else {
        setError('Failed to load platform data.');
      }
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await api.get('/api/platform/users/', { params: { search } });
      setUsers(res.data.users || []);
    } catch { /* handled by main error */ }
  }, [search]);

  useEffect(() => { if (tab === 1) fetchUsers(); }, [tab, fetchUsers]);

  const handleViewDetail = async (companyId) => {
    try {
      const res = await api.get(`/api/platform/companies/${companyId}/`);
      setDetailDialog(res.data);
    } catch {
      setError('Failed to load company details.');
    }
  };

  const handleImpersonate = async (companyId, companyName) => {
    try {
      const res = await api.post(`/api/platform/impersonate/${companyId}/`);
      setImpersonateDialog({ ...res.data, company_name: companyName });
    } catch {
      setError('Failed to impersonate company.');
    }
  };

  const confirmImpersonate = () => {
    if (impersonateDialog) {
      localStorage.setItem('access_token', impersonateDialog.access);
      localStorage.setItem('refresh_token', impersonateDialog.refresh);
      window.location.href = '/dashboard';
    }
  };

  if (error && !loading && !stats.total_companies) {
    return (
      <Box sx={{ p: 4, maxWidth: 600, mx: 'auto', mt: 8 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.mode === 'dark' ? '#0a0e27' : '#f0f2f5', p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} color="text.primary">
            Firmflow Platform Admin
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Owner-level dashboard across all companies
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Chip label="Platform Owner" color="primary" size="small" />
          <IconButton onClick={fetchAll} size="small"><RefreshIcon /></IconButton>
        </Box>
      </Box>

      {loading && !stats.total_companies ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box>
      ) : (
        <>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {/* Stats Row */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={2.4}>
              <StatCard title="Total Companies" value={stats.total_companies || 0} icon={<BusinessIcon />} color={theme.palette.primary.main} />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <StatCard title="Total Users" value={stats.total_users || 0} icon={<PeopleIcon />} color={theme.palette.info.main} />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <StatCard title="Total Clients" value={stats.total_clients || 0} icon={<PersonAddIcon />} color={theme.palette.success.main} />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <StatCard title="MRR" value={formatCurrency(stats.mrr)} icon={<MoneyIcon />} color={theme.palette.warning.main} />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <StatCard title="New This Month" value={stats.new_companies_this_month || 0} icon={<TrendingIcon />} color={theme.palette.success.main} />
            </Grid>
          </Grid>

          {/* Tabs */}
          <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, mb: 3 }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2 }}>
              <Tab label="Companies" />
              <Tab label="Users" />
              <Tab label="Revenue" />
              <Tab label="Activity" />
            </Tabs>
          </Paper>

          {/* Tab: Companies */}
          {tab === 0 && (
            <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
              <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField
                  size="small" placeholder="Search companies..."
                  value={search} onChange={(e) => setSearch(e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                  sx={{ minWidth: 280 }}
                />
                <FormControl size="small" sx={{ minWidth: 130 }}>
                  <InputLabel>Status</InputLabel>
                  <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Company</TableCell>
                      <TableCell>Owner</TableCell>
                      <TableCell align="right">Users</TableCell>
                      <TableCell align="right">Clients</TableCell>
                      <TableCell align="right">Revenue</TableCell>
                      <TableCell>Plan</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell>Last Active</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {companies.map((c) => (
                      <TableRow key={c.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{c.name}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{c.owner_email}</Typography>
                        </TableCell>
                        <TableCell align="right">{c.user_count}</TableCell>
                        <TableCell align="right">{c.client_count}</TableCell>
                        <TableCell align="right">{formatCurrency(c.total_revenue)}</TableCell>
                        <TableCell>
                          <Chip label={c.seat_plan} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>{formatDate(c.created_at)}</TableCell>
                        <TableCell>{c.last_activity ? timeAgo(c.last_activity) : '—'}</TableCell>
                        <TableCell align="center">
                          <Tooltip title="View Details">
                            <IconButton size="small" onClick={() => handleViewDetail(c.id)}>
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Impersonate">
                            <IconButton size="small" onClick={() => handleImpersonate(c.id, c.name)} color="warning">
                              <ImpersonateIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                    {companies.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">No companies found</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Tab: Users */}
          {tab === 1 && (
            <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
              <Box sx={{ p: 2 }}>
                <TextField
                  size="small" placeholder="Search users..."
                  value={search} onChange={(e) => setSearch(e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                  sx={{ minWidth: 280 }}
                />
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Company</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Joined</TableCell>
                      <TableCell>Last Login</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((u) => (
                      <TableRow key={u.id} hover>
                        <TableCell>{u.name || '—'}</TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell>{u.company}</TableCell>
                        <TableCell><Chip label={u.role} size="small" variant="outlined" /></TableCell>
                        <TableCell>{formatDate(u.created_at)}</TableCell>
                        <TableCell>{u.last_login ? timeAgo(u.last_login) : '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Tab: Revenue */}
          {tab === 2 && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
                  <Typography variant="body2" color="text.secondary">Monthly Recurring Revenue</Typography>
                  <Typography variant="h4" fontWeight={700}>{formatCurrency(revenue.mrr)}</Typography>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="body2" color="text.secondary">Annual Run Rate</Typography>
                  <Typography variant="h5" fontWeight={600}>{formatCurrency(revenue.arr)}</Typography>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="body2" color="text.secondary">Churn Rate</Typography>
                  <Typography variant="h6" fontWeight={600} color={revenue.churn_rate > 5 ? 'error' : 'success.main'}>
                    {revenue.churn_rate || 0}%
                  </Typography>
                </Paper>
                <Paper elevation={0} sx={{ p: 3, mt: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Revenue by Plan</Typography>
                  {(revenue.revenue_by_plan || []).map((p) => (
                    <Box key={p.plan} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                      <Typography variant="body2">{p.plan} ({p.count} companies)</Typography>
                      <Typography variant="body2" fontWeight={600}>{formatCurrency(p.amount)}/mo</Typography>
                    </Box>
                  ))}
                </Paper>
                <Paper elevation={0} sx={{ p: 3, mt: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Top Companies by Revenue</Typography>
                  {(revenue.top_companies || []).slice(0, 5).map((c, i) => (
                    <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                      <Typography variant="body2">{c.name}</Typography>
                      <Typography variant="body2" fontWeight={600}>{formatCurrency(c.revenue)}</Typography>
                    </Box>
                  ))}
                </Paper>
              </Grid>
              <Grid item xs={12} md={8}>
                <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2 }}>Revenue — Last 12 Months</Typography>
                  <ResponsiveContainer width="100%" height={350}>
                    <AreaChart data={revenue.revenue_by_month || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${v}`} />
                      <RechartsTooltip formatter={(v) => formatCurrency(v)} />
                      <Area type="monotone" dataKey="amount" stroke={theme.palette.primary.main} fill={`${theme.palette.primary.main}30`} strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>
            </Grid>
          )}

          {/* Tab: Activity */}
          {tab === 3 && (
            <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
              <Box sx={{ p: 2 }}>
                <Typography variant="subtitle2">Recent Activity Across All Companies</Typography>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Time</TableCell>
                      <TableCell>Company</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell>Detail</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activity.map((a) => (
                      <TableRow key={a.id} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{timeAgo(a.timestamp)}</TableCell>
                        <TableCell>{a.company}</TableCell>
                        <TableCell>{a.user || '—'}</TableCell>
                        <TableCell><Chip label={a.action} size="small" variant="outlined" /></TableCell>
                        <TableCell>{a.detail}</TableCell>
                      </TableRow>
                    ))}
                    {activity.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">No recent activity</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </>
      )}

      {/* Company Detail Dialog */}
      <Dialog open={!!detailDialog} onClose={() => setDetailDialog(null)} maxWidth="md" fullWidth>
        {detailDialog && (
          <>
            <DialogTitle>
              {detailDialog.name}
              <Chip label={detailDialog.platform_subscription_status} size="small" sx={{ ml: 1 }} />
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Email</Typography>
                  <Typography>{detailDialog.company_email || '—'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Phone</Typography>
                  <Typography>{detailDialog.phone || '—'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Plan</Typography>
                  <Typography>{detailDialog.platform_plan} ({detailDialog.seat_count} seats)</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Created</Typography>
                  <Typography>{formatDate(detailDialog.created_at)}</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="body2" color="text.secondary">Users</Typography>
                  <Typography variant="h6">{detailDialog.stats?.user_count}</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="body2" color="text.secondary">Clients</Typography>
                  <Typography variant="h6">{detailDialog.stats?.client_count}</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="body2" color="text.secondary">Invoices</Typography>
                  <Typography variant="h6">{detailDialog.stats?.invoice_count}</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="body2" color="text.secondary">Revenue</Typography>
                  <Typography variant="h6">{formatCurrency(detailDialog.stats?.total_revenue)}</Typography>
                </Grid>
              </Grid>
              <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>Users</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Last Login</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(detailDialog.users || []).map((u) => (
                      <TableRow key={u.id}>
                        <TableCell>{u.name || '—'}</TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell>{u.role}</TableCell>
                        <TableCell>{u.last_login ? timeAgo(u.last_login) : '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailDialog(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Impersonate Confirmation Dialog */}
      <Dialog open={!!impersonateDialog} onClose={() => setImpersonateDialog(null)}>
        <DialogTitle>Impersonate Company</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            You are about to log in as <strong>{impersonateDialog?.user_email}</strong> at <strong>{impersonateDialog?.company_name}</strong>.
            This will replace your current session.
          </Alert>
          <Typography variant="body2">
            You will be logged in as the admin user for this company. To return, log out and sign back in with your platform admin credentials.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImpersonateDialog(null)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={confirmImpersonate}>
            Confirm Impersonate
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
